/**
 * Helpers for formatting event date and time on the registration screen
 * in the user's selected language (e.g. Dutch "13 juni 2026", German 24h time).
 */
/**
 * Resolve BCP 47 locale from event language or i18n code (e.g. "nl" → "nl-NL").
 * Prefers primary company/language locale when available.
 */
export function getRegistrationDateLocale(eventLanguages, selectedLanguageId, i18nLanguage) {
    if (Array.isArray(eventLanguages) && selectedLanguageId) {
        const lang = eventLanguages.find((l) => l.id === selectedLanguageId);
        if (lang?.locale?.trim())
            return lang.locale.trim();
        if (lang?.code?.trim())
            return lang.code.trim();
    }
    const code = (i18nLanguage || 'en').toLowerCase().split('-')[0];
    const map = { nl: 'nl-NL', de: 'de-DE', en: 'en-US' };
    return map[code] || 'en-US';
}
/**
 * Parse "DD / MM / YYYY" or "DD/MM/YYYY" (day/month/year) as used by the date range picker display.
 */
function parseDDMMYYYY(s) {
    const parts = s.split(/\s*\/\s*/).map((p) => parseInt(p.trim(), 10));
    if (parts.length !== 3 || parts.some((n) => isNaN(n)))
        return null;
    const [d, m, y] = parts;
    if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > 2100)
        return null;
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d)
        return null;
    return date;
}
/** Parse YYYY-MM-DD (or ISO datetime prefix) as a local calendar date. */
function parseISODateLocal(s) {
    const dateOnly = s.trim().split('T')[0];
    const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match)
        return null;
    const y = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const d = parseInt(match[3], 10);
    if (m < 1 || m > 12 || d < 1 || d > 31)
        return null;
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d)
        return null;
    return date;
}
/** Returns true when a date value is missing or a .NET DateTime.MinValue sentinel. */
function isUnsetDateValue(val) {
    if (val == null)
        return true;
    const s = String(val).trim();
    if (!s)
        return true;
    return /^0001-01-01/i.test(s);
}
function formatDateLocal(date, locale) {
    return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
}
/** Parse backend fallback dates like "January 27, 2026" as local calendar dates. */
function parseLongMonthDate(s) {
    const trimmed = s.trim();
    if (!trimmed)
        return null;
    const parsed = new Date(trimmed);
    if (isNaN(parsed.getTime()))
        return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}
/** Re-localize backend English formattedDate for the selected registration locale. */
function convertFormattedDateFallbackToLocale(formattedDate, locale) {
    const trimmed = formattedDate.trim();
    if (!trimmed)
        return '';
    const parts = trimmed.split(/\s+-\s+/);
    if (parts.length === 2) {
        const start = parseLongMonthDate(parts[0].trim());
        const end = parseLongMonthDate(parts[1].trim());
        if (start && end) {
            return `${formatDateLocal(start, locale)} - ${formatDateLocal(end, locale)}`;
        }
    }
    const single = parseLongMonthDate(trimmed);
    return single ? formatDateLocal(single, locale) : trimmed;
}
/** Parse a single date string; if it contains " - ", use first segment for start, second for end. */
function parseDateValue(val, segment = 'start') {
    const parts = val.trim().split(/\s+-\s+/);
    const s = segment === 'end' && parts.length > 1 ? parts[1].trim() : (parts[0] || '').trim();
    if (!s)
        return null;
    const isoLocal = parseISODateLocal(s);
    if (isoLocal)
        return isoLocal;
    const ddmm = parseDDMMYYYY(s);
    if (ddmm)
        return ddmm;
    const longMonth = parseLongMonthDate(s);
    if (longMonth)
        return longMonth;
    return null;
}
/**
 * Format event date range in the given locale (e.g. Dutch: "13 juni 2026 - 14 juni 2026").
 */
export function formatRegistrationDate(input, locale) {
    const fallback = () => {
        const formatted = input?.formattedDate?.trim();
        return formatted ? convertFormattedDateFallbackToLocale(formatted, locale) : '';
    };
    const startDateVal = input?.startDate;
    if (isUnsetDateValue(startDateVal))
        return fallback();
    try {
        const startStrVal = String(startDateVal);
        const start = parseDateValue(startStrVal, 'start');
        if (!start)
            return fallback();
        const endDateVal = input?.endDate;
        const end = !isUnsetDateValue(endDateVal)
            ? parseDateValue(String(endDateVal), 'start')
            : (parseDateValue(startStrVal, 'end') ?? start);
        if (!end)
            return formatDateLocal(start, locale);
        const startStr = formatDateLocal(start, locale);
        if (isUnsetDateValue(endDateVal) || start.getTime() === end.getTime())
            return startStr;
        const endStr = formatDateLocal(end, locale);
        return `${startStr} - ${endStr}`;
    }
    catch {
        return fallback();
    }
}
/**
 * Parse 12-hour time "12:10 AM", "5:19 PM", "12:00 PM" etc.
 */
function parse12HourTime(t) {
    const match = t.trim().match(/^(\d{1,2})\s*:\s*(\d{2})(?:\s*:\s*(\d{2}))?\s*(AM|PM)$/i);
    if (!match)
        return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const isPm = (match[4] || '').toUpperCase() === 'PM';
    if (hours === 12)
        hours = isPm ? 12 : 0;
    else if (isPm)
        hours += 12;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59)
        return null;
    return { hours, minutes };
}
/** Build a Date from date part and time string (ISO, "HH:mm", or "12:10 AM"). */
function parseDateTime(datePart, timeStr) {
    const t = timeStr.trim();
    const dateOnly = (datePart || '1970-01-01').trim().split('T')[0];
    if (t.includes('T') || t.includes('Z')) {
        const d = new Date(t);
        return isNaN(d.getTime()) ? null : d;
    }
    const twelve = parse12HourTime(t);
    if (twelve != null) {
        const [y, m, day] = dateOnly.split('-').map((n) => parseInt(n, 10));
        const date = new Date(y, (m || 1) - 1, day || 1, twelve.hours, twelve.minutes, 0, 0);
        return isNaN(date.getTime()) ? null : date;
    }
    const combined = `${dateOnly}T${t}`;
    const d = new Date(combined);
    return isNaN(d.getTime()) ? null : d;
}
/** Returns true when a time value is missing or a .NET DateTime.MinValue sentinel. */
function isUnsetTimeValue(val) {
    if (val == null)
        return true;
    const s = String(val).trim();
    if (!s)
        return true;
    return /^0001-01-01/i.test(s);
}
function formatHoursMinutes(hours, minutes, locale) {
    const date = new Date(2000, 0, 1, hours, minutes);
    return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}
/** Convert a single 12-hour label (e.g. "10:00 AM") to 24-hour locale format. */
function convert12HourLabelTo24Hour(label, locale) {
    const parsed = parse12HourTime(label);
    if (!parsed)
        return label;
    return formatHoursMinutes(parsed.hours, parsed.minutes, locale);
}
/** Convert backend fallback formattedTime (12-hour) to 24-hour display. */
function convertFormattedTimeFallbackTo24Hour(formattedTime, locale) {
    const trimmed = formattedTime.trim();
    if (!trimmed)
        return '';
    const parts = trimmed.split(/\s+-\s+/);
    if (parts.length === 2) {
        return `${convert12HourLabelTo24Hour(parts[0].trim(), locale)} - ${convert12HourLabelTo24Hour(parts[1].trim(), locale)}`;
    }
    return convert12HourLabelTo24Hour(trimmed, locale);
}
/**
 * Format event time range in 24-hour format (e.g. "13:00 - 17:30").
 */
export function formatRegistrationTime(input, locale) {
    const fallback = () => {
        const formatted = input?.formattedTime?.trim();
        return formatted ? convertFormattedTimeFallbackTo24Hour(formatted, locale) : '';
    };
    const startTimeVal = input?.startTime;
    if (isUnsetTimeValue(startTimeVal))
        return fallback();
    const opts = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    };
    try {
        const startTimeStr = String(startTimeVal).trim();
        const startDatePart = input.startDate
            ? String(input.startDate).trim().split('T')[0]
            : '1970-01-01';
        const endDatePart = input.endDate ? String(input.endDate).trim().split('T')[0] : startDatePart;
        const startDt = parseDateTime(startDatePart, startTimeStr);
        if (!startDt)
            return fallback();
        const endTimeVal = input?.endTime;
        const endDt = !isUnsetTimeValue(endTimeVal)
            ? parseDateTime(endDatePart, String(endTimeVal).trim())
            : startDt;
        if (!endDt)
            return startDt.toLocaleTimeString(locale, opts);
        const startFormatted = startDt.toLocaleTimeString(locale, opts);
        if (isUnsetTimeValue(endTimeVal) || startDt.getTime() === endDt.getTime())
            return startFormatted;
        const endFormatted = endDt.toLocaleTimeString(locale, opts);
        return `${startFormatted} - ${endFormatted}`;
    }
    catch {
        return fallback();
    }
}
